/*
 * itv-x-post-templates.js — the X Post Studio template library
 *
 * WHY THIS FILE IS THE MOST IMPORTANT ONE IN THE TOOL:
 *
 * Surveying the three strongest fake-tweet generators (PostWizard, Pranksters,
 * BrandBird), all three already ship four badge tiers, Community Notes,
 * dim mode and unwatermarked HD export. This category is NOT stale — being
 * current only reaches parity here, which is the opposite of the situation
 * that made the SERP tool win.
 *
 * The one thing NONE of them has is a curated template library. Pranksters
 * states it outright as a non-feature. A library of 60 presets is also the
 * single biggest perceived-value item in the SERP tool. So this file is the
 * wedge, and it is weighted accordingly.
 *
 * ---------------------------------------------------------------------------
 * FICTIONAL ACCOUNTS ONLY, AND ON PURPOSE.
 *
 * Every account here is invented. No real person, journalist, politician or
 * executive appears in any template. Competitors ship presets impersonating
 * named individuals; that hands every visitor a ready-made defamatory post
 * about a real person, published from our domain, and it is not worth the
 * exposure on a store that is already answering one DMCA notice. Users can
 * still type any name they like — that is their post, not ours.
 *
 * Brand-shaped templates use generic company names for the same reason.
 * ---------------------------------------------------------------------------
 *
 * COVERAGE, not prettiness. Between them these presets exercise every
 * rendering mode the tool has — all four badge tiers plus unverified, all
 * three themes, both viewports, both layouts, 1/2/3/4-image grids, quote
 * tweets, reply chains, threads, polls, Community Notes, promoted and pinned.
 * A user should be able to see everything the tool can draw before deciding
 * whether to pay for anything, whenever that decision gets made.
 *
 * The `state` of each template is a PARTIAL. itvXPost.applyTemplate merges it
 * over defaultState(), so a template only states what it changes.
 */
(function (window) {
    'use strict';

    var HOUR = 3600e3, DAY = 24 * HOUR;
    function ago(ms) { return new Date(Date.now() - ms).toISOString(); }

    /* Builder. Keeps each entry to the fields that actually differ, so the
       library stays readable at fifty entries instead of becoming a wall of
       repeated defaults. */
    function mk(id, category, name, description, s) {
        s = s || {};
        var state = {
            theme: s.theme || 'light',
            viewport: s.viewport || 'desktop',
            layout: s.layout || 'detail',
            author: Object.assign(
                { name: '', handle: '', avatar: '', badge: 'none',
                  affiliateIcon: '', protected: false },
                s.author || {}),
            post: Object.assign(
                { text: '', media: [], altBadge: false,
                  timestamp: s.at || ago(3 * HOUR), source: '', dir: 'ltr' },
                s.post || {}),
            metrics: Object.assign(
                { replies: 0, reposts: 0, quotes: 0, likes: 0, bookmarks: 0, views: 0 },
                s.metrics || {}),
            flags: Object.assign(
                { liked: false, reposted: false, bookmarked: false,
                  promoted: false, pinned: false },
                s.flags || {}),
            quoted: s.quoted || null,
            note: s.note || null,
            poll: s.poll || null,
            thread: s.thread || [],
            replies: s.replies || []
        };
        return { id: id, category: category, name: name, description: description, state: state };
    }

    /* A quoted post, a reply and a thread entry are all small shapes; these
       keep the entries below legible. */
    function quo(name, handle, badge, text, atMs) {
        return {
            author: { name: name, handle: handle, avatar: '', badge: badge || 'none' },
            post: { text: text, media: [], dir: 'ltr', timestamp: ago(atMs || 6 * HOUR) }
        };
    }
    function rep(name, handle, badge, text, likes) {
        return {
            author: { name: name, handle: handle, avatar: '', badge: badge || 'none' },
            post: { text: text, media: [], dir: 'ltr', timestamp: ago(1 * HOUR) },
            metrics: { replies: 0, reposts: 0, likes: likes || 0, views: 0 }
        };
    }
    function thr(text, likes) {
        return {
            text: text, media: [], dir: 'ltr', timestamp: ago(2 * HOUR),
            metrics: { replies: 0, reposts: 0, likes: likes || 0, views: 0 }
        };
    }

    window.ITV_X_TEMPLATES = [

        /* ================= 1. BREAKING NEWS (4) ================= */

        mk(1, 'news', 'Breaking news alert',
           'Grey-check wire account, heavy view count, single post',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Global News Wire', handle: 'globalnewswire', badge: 'grey' },
               post: { text: 'BREAKING: Transport authority confirms all services will resume by 06:00 local time after last night\'s system-wide outage.', source: 'Twitter Web App' },
               metrics: { replies: 2841, reposts: 12400, quotes: 1830, likes: 31200, bookmarks: 2100, views: 4820000 }
           }),

        mk(2, 'news', 'Live updates thread',
           'Three-post thread, gold badge, timeline layout',
           {
               theme: 'dim', layout: 'feed',
               author: { name: 'Northgate Herald', handle: 'northgateherald', badge: 'gold' },
               post: { text: 'LIVE: We are covering this morning\'s budget announcement. Updates in this thread. 🧵' },
               metrics: { replies: 412, reposts: 1980, quotes: 240, likes: 5600, views: 890000 },
               thread: [
                   thr('1/ The chancellor has confirmed the headline rate will remain unchanged for the next two years.', 820),
                   thr('2/ Fuel duty frozen again. Third consecutive year.', 640)
               ]
           }),

        mk(3, 'news', 'Correction notice',
           'A publication retracting — unverified reply chain below',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'The Daily Ledger', handle: 'dailyledger', badge: 'gold' },
               post: { text: 'CORRECTION: An earlier version of this story stated the figure was £4.2bn. The correct figure is £4.2m. We have updated the article and apologise for the error.' },
               metrics: { replies: 1840, reposts: 3200, quotes: 2900, likes: 6100, views: 1240000 },
               replies: [
                   rep('Sam Okafor', 'samokafor', 'none', 'That is quite a typo.', 4200),
                   rep('Priya R', 'priyar_', 'none', 'Three orders of magnitude!', 1800)
               ]
           }),

        mk(4, 'news', 'Photo report, 4 images',
           'Four-image grid with ALT badges, mobile viewport',
           {
               theme: 'dark', viewport: 'mobile', layout: 'detail',
               author: { name: 'Frontline Photo', handle: 'frontlinephoto', badge: 'blue' },
               post: {
                   text: 'Scenes from the harbour this morning as the clean-up begins.',
                   altBadge: true,
                   media: [{ src: '', alt: '' }, { src: '', alt: '' }, { src: '', alt: '' }, { src: '', alt: '' }]
               },
               metrics: { replies: 210, reposts: 1400, likes: 9800, views: 420000 }
           }),

        /* ================= 2. BRAND & PR (4) ================= */

        mk(5, 'brand', 'Product launch',
           'Gold business badge, square avatar, single image',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Lumen Audio', handle: 'lumenaudio', badge: 'gold' },
               post: {
                   text: 'Introducing Lumen One. 40-hour battery, adaptive noise cancelling, and a price we think you\'ll like.\n\nPre-orders open now.',
                   media: [{ src: '', alt: '' }]
               },
               metrics: { replies: 890, reposts: 4200, quotes: 610, likes: 24000, bookmarks: 3100, views: 1900000 }
           }),

        mk(6, 'brand', 'The apology post',
           'Long-form crisis statement, replies visible',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Vantage Rail', handle: 'vantagerail', badge: 'gold' },
               post: { text: 'We know yesterday fell far short of what you expect from us. Every affected passenger will be contacted directly with a full refund by Friday. We are publishing the independent review in full next week.' },
               metrics: { replies: 12400, reposts: 3100, quotes: 8900, likes: 4200, views: 6100000 },
               replies: [
                   rep('Dan H', 'danh_travels', 'none', 'Four hours on a platform with no announcements.', 8900),
                   rep('Meera', 'meerawrites', 'none', 'Publishing the review in full is the right call.', 2100)
               ]
           }),

        mk(7, 'brand', 'Customer service reply',
           'Reply layout — brand replying to a customer',
           {
               theme: 'dim', layout: 'feed',
               author: { name: 'Orbit Bank', handle: 'orbitbank', badge: 'gold' },
               post: { text: 'Sorry to hear that. Can you send us a DM with your reference number and we\'ll pick this up straight away? ^JM' },
               metrics: { replies: 12, reposts: 4, likes: 86, views: 24000 },
               quoted: quo('Alex Fenn', 'alexfenn', 'none', 'Third day in a row the app won\'t let me log in. @orbitbank any update?', 8 * HOUR)
           }),

        mk(8, 'brand', 'Promoted post',
           'Advertising label, affiliate badge, feed layout',
           {
               theme: 'light', layout: 'feed',
               author: { name: 'Meridian Travel', handle: 'meridiantravel', badge: 'gold' },
               post: {
                   text: 'Winter fares from £39. Book by Sunday.',
                   media: [{ src: '', alt: '' }, { src: '', alt: '' }]
               },
               metrics: { replies: 34, reposts: 120, likes: 1400, views: 780000 },
               flags: { promoted: true }
           }),

        /* ================= 3. GOVERNMENT & POLITICS (4) ================= */

        mk(9, 'politics', 'Official government notice',
           'Grey badge, formal statement, pinned',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Dept. for Transport', handle: 'depttransport', badge: 'grey' },
               post: { text: 'Severe weather warning in effect from 18:00 today until 09:00 tomorrow. Avoid non-essential travel on the eastern network.' },
               metrics: { replies: 1200, reposts: 8900, quotes: 340, likes: 4100, views: 2200000 },
               flags: { pinned: true }
           }),

        mk(10, 'politics', 'Statement with Community Note',
           'A claim, publicly fact-checked underneath',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Rt Hon J. Whitfield MP', handle: 'jwhitfieldmp', badge: 'grey' },
               post: { text: 'Unemployment is now at its lowest level in forty years — the lowest in the entire G7.' },
               metrics: { replies: 8900, reposts: 2100, quotes: 12000, likes: 3400, views: 5400000 },
               note: {
                   headline: 'Readers added context',
                   body: 'The figure cited refers to a single quarter and uses a definition that excludes part-time workers seeking full-time employment. Two G7 countries reported lower rates in the same period.',
                   helpful: true
               }
           }),

        mk(11, 'politics', 'The quote-tweet dunk',
           'Quoting an opponent — the highest-engagement political shape',
           {
               theme: 'dim', layout: 'detail',
               author: { name: 'Policy Watch', handle: 'policywatch', badge: 'blue' },
               post: { text: 'This was posted four hours after the department published the exact opposite figure on its own website.' },
               metrics: { replies: 3400, reposts: 18000, quotes: 900, likes: 62000, bookmarks: 4100, views: 3900000 },
               quoted: quo('Rt Hon J. Whitfield MP', 'jwhitfieldmp', 'grey', 'Unemployment is now at its lowest level in forty years.', 10 * HOUR)
           }),

        mk(12, 'politics', 'Campaign announcement',
           'Mobile viewport, dark theme, high replies',
           {
               theme: 'dark', viewport: 'mobile', layout: 'detail',
               author: { name: 'Ada Cole', handle: 'adacole', badge: 'blue' },
               post: { text: 'I\'m standing for the Northgate seat.\n\nI grew up here. I want to fix the buses, the housing, and the fact that nobody under thirty can afford to stay.\n\nLet\'s go.' },
               metrics: { replies: 4100, reposts: 9800, quotes: 1200, likes: 88000, views: 4100000 }
           }),

        /* ================= 4. VIRAL & SHITPOST (5) ================= */

        mk(13, 'viral', 'The one-liner',
           'Unverified account, enormous ratio — the classic viral shape',
           {
               theme: 'dark', layout: 'detail',
               author: { name: 'liv', handle: 'livdoesthings', badge: 'none' },
               post: { text: 'my landlord asked if i could water his plants while he\'s away and i said sure and then i asked if he could fix the boiler while he\'s here and apparently that was rude' },
               metrics: { replies: 1900, reposts: 84000, quotes: 3200, likes: 612000, bookmarks: 41000, views: 18400000 },
               flags: { liked: true }
           }),

        mk(14, 'viral', 'The ratio',
           'A bad take with replies far exceeding likes',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Marcus Reed', handle: 'marcusreedceo', badge: 'blue' },
               post: { text: 'If you can\'t afford a house it\'s because you\'re buying coffee. I bought my first property at 23 with nothing but hard work and a small loan from my father.' },
               metrics: { replies: 94000, reposts: 2100, quotes: 41000, likes: 1800, views: 22000000 }
           }),

        mk(15, 'viral', 'Screenshot dunk with reply chain',
           'Quote tweet plus three replies, dim theme',
           {
               theme: 'dim', layout: 'feed',
               author: { name: 'nat', handle: 'natgotjokes', badge: 'none' },
               post: { text: 'sir this is a group chat about a birthday cake' },
               metrics: { replies: 340, reposts: 12000, likes: 98000, views: 3400000 },
               quoted: quo('Marcus Reed', 'marcusreedceo', 'blue', 'Let me explain why this is actually a supply chain problem.', 5 * HOUR),
               replies: [
                   rep('joe', 'joeeeee', 'none', 'not the supply chain 😭', 12000),
                   rep('Hana', 'hanacodes', 'none', 'he does this in every thread', 4300)
               ]
           }),

        mk(16, 'viral', 'Two-image meme',
           'Mobile, two-image grid, unverified',
           {
               theme: 'dark', viewport: 'mobile', layout: 'detail',
               author: { name: 'sam', handle: 'samsends', badge: 'none' },
               post: { text: 'what i ordered vs what arrived', media: [{ src: '', alt: '' }, { src: '', alt: '' }] },
               metrics: { replies: 890, reposts: 34000, likes: 240000, views: 8900000 }
           }),

        mk(17, 'viral', 'The three-image thread starter',
           '3-image grid, thread continuation, blue check',
           {
               theme: 'light', layout: 'feed',
               author: { name: 'Ren Ito', handle: 'renito', badge: 'blue' },
               post: {
                   text: 'I restored a 1974 camera that had been in a drawer for thirty years. Thread of how it went.',
                   media: [{ src: '', alt: '' }, { src: '', alt: '' }, { src: '', alt: '' }]
               },
               metrics: { replies: 420, reposts: 8900, likes: 74000, bookmarks: 12000, views: 2100000 },
               thread: [thr('The shutter was seized solid. Two days in isopropyl and it finally moved.', 8900)]
           }),

        /* ================= 5. CELEBRITY (4) ================= */

        mk(18, 'celebrity', 'Tour announcement',
           'Blue badge, huge metrics, single image',
           {
               theme: 'dark', layout: 'detail',
               author: { name: 'AURELIA', handle: 'aurelia', badge: 'blue' },
               post: { text: 'WORLD TOUR 2027. Tickets Friday 10am local. 🖤', media: [{ src: '', alt: '' }] },
               metrics: { replies: 84000, reposts: 340000, quotes: 41000, likes: 2400000, bookmarks: 190000, views: 62000000 }
           }),

        mk(19, 'celebrity', 'Fan reply chain',
           'Feed layout, celebrity post with three fan replies',
           {
               theme: 'dim', layout: 'feed',
               author: { name: 'Devon Marsh', handle: 'devonmarsh', badge: 'blue' },
               post: { text: 'Wrapped filming today. Eleven months. I\'m going to sleep for a week.' },
               metrics: { replies: 12000, reposts: 34000, likes: 480000, views: 9800000 },
               replies: [
                   rep('mia 🎬', 'miawatches', 'none', 'YOU DESERVE THE REST', 8900),
                   rep('Cal', 'calfilm', 'none', 'eleven months is insane, congratulations', 2100),
                   rep('Rosa', 'rosaonfilm', 'none', 'cannot wait for this one', 940)
               ]
           }),

        mk(20, 'celebrity', 'Affiliate badge account',
           'Organisation-linked account — the badge tier nobody models',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Jules Petrov', handle: 'julespetrov', badge: 'affiliate', affiliateIcon: '' },
               post: { text: 'Delighted to be joining the team as creative director. More soon.' },
               metrics: { replies: 890, reposts: 2100, likes: 34000, views: 1200000 }
           }),

        mk(21, 'celebrity', 'Protected account',
           'Locked account — rarely modelled and instantly recognisable',
           {
               theme: 'light', viewport: 'mobile', layout: 'detail',
               author: { name: 'K.', handle: 'private_k', badge: 'none', protected: true },
               post: { text: 'only posting here now. much quieter.' },
               metrics: { replies: 42, reposts: 3, likes: 890, views: 12000 }
           }),

        /* ================= 6. TECH & STARTUPS (4) ================= */

        mk(22, 'tech', 'Founder announcement',
           'Funding post, blue badge, bookmarked',
           {
               theme: 'dark', layout: 'detail',
               author: { name: 'Ines Duarte', handle: 'inesduarte', badge: 'blue' },
               post: { text: 'We raised $12M Series A to keep building.\n\nWhen we started this in a rented room in 2023 we had four users and three of them were my family.\n\nWe now have 180,000.' },
               metrics: { replies: 1200, reposts: 4100, quotes: 340, likes: 42000, bookmarks: 8900, views: 2400000 },
               flags: { bookmarked: true }
           }),

        mk(23, 'tech', 'The layoffs post',
           'Long statement, heavy quote count',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Northwind Systems', handle: 'northwindsys', badge: 'gold' },
               post: { text: 'Today we made the difficult decision to reduce our team by 12%. Everyone affected has been notified, and will receive six months\' salary, healthcare through the end of the year, and immediate equity vesting.' },
               metrics: { replies: 4100, reposts: 1800, quotes: 12000, likes: 3400, views: 4800000 }
           }),

        mk(24, 'tech', 'Hot take thread',
           'Three-post thread, dim theme, feed layout',
           {
               theme: 'dim', layout: 'feed',
               author: { name: 'Tobi Adeyemi', handle: 'tobiwrites', badge: 'blue' },
               post: { text: 'Unpopular opinion: most engineering teams do not have a testing problem. They have a "nobody agrees what this feature is supposed to do" problem. 🧵' },
               metrics: { replies: 890, reposts: 6100, quotes: 420, likes: 34000, bookmarks: 18000, views: 1900000 },
               thread: [
                   thr('You can have 100% coverage on the wrong behaviour. That is not a safety net, it is a very expensive way to freeze a misunderstanding.', 4100),
                   thr('The fix is boring: write down what it should do, in a sentence, before anyone opens an editor.', 2800)
               ]
           }),

        mk(25, 'tech', 'Outage status update',
           'Status account, grey badge, mobile',
           {
               theme: 'dark', viewport: 'mobile', layout: 'detail',
               author: { name: 'Northwind Status', handle: 'northwindstatus', badge: 'grey' },
               post: { text: 'Update 14:20 UTC — We have identified the cause and are rolling out a fix. API error rates are falling. Next update in 30 minutes.' },
               metrics: { replies: 340, reposts: 890, likes: 2100, views: 420000 }
           }),

        /* ================= 7. SPORTS (4) ================= */

        mk(26, 'sports', 'Full-time score',
           'Club account, gold badge, single image',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Northgate United', handle: 'northgateutd', badge: 'gold' },
               post: { text: 'FULL TIME.\n\nNorthgate 3–2 Ashvale.\n\nWhat a night. 🔵⚪', media: [{ src: '', alt: '' }] },
               metrics: { replies: 4100, reposts: 18000, quotes: 1200, likes: 120000, views: 6800000 }
           }),

        mk(27, 'sports', 'Transfer rumour',
           'Journalist account with the classic "HERE WE GO" shape',
           {
               theme: 'dark', layout: 'detail',
               author: { name: 'Transfer Desk', handle: 'transferdesk', badge: 'blue' },
               post: { text: 'DONE DEAL: Northgate have completed the signing of the 21-year-old midfielder on a five-year contract. Medical passed this morning. Announcement expected within hours. 🔵✅' },
               metrics: { replies: 8900, reposts: 42000, quotes: 6100, likes: 240000, bookmarks: 12000, views: 14000000 }
           }),

        mk(28, 'sports', 'Fan reaction with replies',
           'Feed layout, unverified fan, quote of the club',
           {
               theme: 'dim', layout: 'feed',
               author: { name: 'Ash', handle: 'ashNGU', badge: 'none' },
               post: { text: 'I have supported this club for 24 years and I have never seen a second half like that' },
               metrics: { replies: 210, reposts: 890, likes: 12000, views: 340000 },
               quoted: quo('Northgate United', 'northgateutd', 'gold', 'FULL TIME. Northgate 3–2 Ashvale.', 2 * HOUR),
               replies: [rep('Ellie', 'ellieNGU', 'none', 'I lost my voice completely', 890)]
           }),

        mk(29, 'sports', 'Match-day poll',
           'Poll block plus club branding',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Northgate United', handle: 'northgateutd', badge: 'gold' },
               post: { text: 'Man of the match?' },
               metrics: { replies: 1200, reposts: 340, likes: 8900, views: 890000 },
               poll: {
                   options: [
                       { label: 'Ferreira', votes: 8400 },
                       { label: 'Ndiaye', votes: 4200 },
                       { label: 'Kowalski', votes: 2100 },
                       { label: 'Someone else', votes: 900 }
                   ],
                   totalVotes: 15600, ended: false, remaining: '4h'
               }
           }),

        /* ================= 8. COMMUNITY NOTES (4) ================= */

        mk(30, 'notes', 'Note on a viral claim',
           'The canonical fact-check shape',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Daily Facts', handle: 'dailyfactsfeed', badge: 'none' },
               post: { text: 'BREAKING: Scientists confirm drinking two litres of lemon water a day removes 90% of toxins from the body.' },
               metrics: { replies: 12000, reposts: 34000, quotes: 8900, likes: 41000, views: 12000000 },
               note: {
                   headline: 'Readers added context',
                   body: 'No such study exists. The claim appears to originate from a blog post that cites a paper on citrus antioxidants which does not mention toxin removal. The liver and kidneys perform this function and are not affected by lemon water.',
                   helpful: true
               }
           }),

        mk(31, 'notes', 'Note on a miscaptioned photo',
           'Image plus a note correcting its context',
           {
               theme: 'dim', layout: 'detail',
               author: { name: 'World Updates', handle: 'worldupdatesnow', badge: 'none' },
               post: { text: 'Incredible scenes in the capital tonight.', media: [{ src: '', alt: '' }] },
               metrics: { replies: 4100, reposts: 18000, quotes: 12000, likes: 22000, views: 8400000 },
               note: {
                   headline: 'Readers added context',
                   body: 'This photograph was taken in 2019 and shows a different city. A reverse image search returns the original agency caption.',
                   helpful: true
               }
           }),

        mk(32, 'notes', 'Note on a brand claim',
           'Gold badge with a note — the combination brands fear',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'PureLeaf Wellness', handle: 'pureleafwellness', badge: 'gold' },
               post: { text: 'Clinically proven to boost your metabolism by 300%.' },
               metrics: { replies: 8900, reposts: 2100, quotes: 6100, likes: 1200, views: 3400000 },
               note: {
                   headline: 'Readers added context',
                   body: 'The cited trial had 12 participants, no control group, and was funded by the manufacturer. "Clinically proven" is not a regulated term in this jurisdiction.',
                   helpful: true
               }
           }),

        mk(33, 'notes', 'Note on a mobile viewport',
           'Dark mode, phone width — how a note actually looks in the app',
           {
               theme: 'dark', viewport: 'mobile', layout: 'detail',
               author: { name: 'Crypto Signals', handle: 'cryptosignalspro', badge: 'blue' },
               post: { text: 'This coin is guaranteed to 10x by the end of the month. Not financial advice.' },
               metrics: { replies: 2100, reposts: 890, quotes: 4100, likes: 3400, views: 1800000 },
               note: {
                   headline: 'Readers added context',
                   body: 'No asset return can be guaranteed. This account has posted the same claim about 14 different assets in the past 60 days.',
                   helpful: true
               }
           }),

        /* ================= 9. MARKETING & SOCIAL PROOF (4) ================= */

        mk(34, 'marketing', 'Customer testimonial',
           'The landing-page screenshot — the top commercial use case',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Hannah Weiss', handle: 'hannahbuilds', badge: 'blue' },
               post: { text: 'Switched our whole team over last month. Onboarding took an afternoon instead of the two weeks I\'d budgeted for. Genuinely the easiest migration I\'ve done.' },
               metrics: { replies: 24, reposts: 89, quotes: 12, likes: 1240, bookmarks: 210, views: 84000 }
           }),

        mk(35, 'marketing', 'Founder-to-customer reply',
           'Social proof through a visible reply exchange',
           {
               theme: 'light', layout: 'feed',
               author: { name: 'Ines Duarte', handle: 'inesduarte', badge: 'blue' },
               post: { text: 'This genuinely made our week. Thank you Hannah — the onboarding rewrite took us four months and this is exactly what we were hoping for.' },
               metrics: { replies: 8, reposts: 34, likes: 620, views: 42000 },
               quoted: quo('Hannah Weiss', 'hannahbuilds', 'blue', 'Switched our whole team over last month. Onboarding took an afternoon.', 4 * HOUR)
           }),

        mk(36, 'marketing', 'Milestone post',
           'Round-number announcement with an image',
           {
               theme: 'dim', layout: 'detail',
               author: { name: 'Lumen Audio', handle: 'lumenaudio', badge: 'gold' },
               post: { text: '100,000 pairs shipped.\n\nThank you, all of you. 🎧', media: [{ src: '', alt: '' }] },
               metrics: { replies: 340, reposts: 1200, likes: 18000, bookmarks: 890, views: 940000 }
           }),

        mk(37, 'marketing', 'Case-study thread',
           'Numbers thread, bookmarked heavily — the B2B shape',
           {
               theme: 'light', layout: 'feed',
               author: { name: 'Growth Notes', handle: 'growthnotes', badge: 'blue' },
               post: { text: 'We cut churn from 8.2% to 3.1% in two quarters without touching pricing. Here is exactly what we changed. 🧵' },
               metrics: { replies: 210, reposts: 3400, quotes: 180, likes: 21000, bookmarks: 34000, views: 1400000 },
               thread: [
                   thr('1. We stopped asking "why did you leave" in the cancellation flow and started asking "what were you trying to do when you signed up".', 2100),
                   thr('2. 61% of churned accounts had never completed a single core action. That is an onboarding problem wearing a pricing costume.', 3400)
               ]
           }),

        /* ================= 10. PRANK & MEME (5) ================= */

        mk(38, 'prank', 'The impossible announcement',
           'The headline arrival intent — a plausible corporate shock post',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Lumen Audio', handle: 'lumenaudio', badge: 'gold' },
               post: { text: 'From January, all headphones will be sold without the left earcup. Customers have told us they only use one at a time.' },
               metrics: { replies: 42000, reposts: 18000, quotes: 34000, likes: 6100, views: 22000000 }
           }),

        mk(39, 'prank', 'Wrong account posted it',
           'The classic "posted from the brand account" gag',
           {
               theme: 'dark', layout: 'detail',
               author: { name: 'Northgate United', handle: 'northgateutd', badge: 'gold' },
               post: { text: 'ok but genuinely who ate my lunch from the staff fridge' },
               metrics: { replies: 8900, reposts: 42000, quotes: 12000, likes: 340000, views: 18000000 }
           }),

        mk(40, 'prank', 'Deleted-and-screenshotted',
           'Quote tweet of something obviously about to be deleted',
           {
               theme: 'dim', layout: 'detail',
               author: { name: 'screenshot guy', handle: 'screenshotguy', badge: 'none' },
               post: { text: 'got it before it went 📸' },
               metrics: { replies: 1200, reposts: 24000, likes: 180000, views: 6100000 },
               quoted: quo('Marcus Reed', 'marcusreedceo', 'blue', 'I have never made a mistake in my professional life and I never will.', 3 * HOUR)
           }),

        mk(41, 'prank', 'Absurd poll',
           'Poll with a landslide — mobile, dark',
           {
               theme: 'dark', viewport: 'mobile', layout: 'detail',
               author: { name: 'jamie', handle: 'jamieasks', badge: 'none' },
               post: { text: 'settle this for me' },
               metrics: { replies: 4100, reposts: 8900, likes: 42000, views: 3400000 },
               poll: {
                   options: [
                       { label: 'It is a sandwich', votes: 1200 },
                       { label: 'It is absolutely not a sandwich', votes: 48000 }
                   ],
                   totalVotes: 49200, ended: true, remaining: ''
               }
           }),

        mk(42, 'prank', 'The pinned confession',
           'Pinned label plus an unverified account',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'greg', handle: 'gregofficial', badge: 'none' },
               post: { text: 'pinning this so it is the first thing everyone sees: I have never once used a semicolon correctly and I am not going to start now' },
               metrics: { replies: 890, reposts: 4100, likes: 62000, views: 2100000 },
               flags: { pinned: true, liked: true }
           }),

        /* ================= 11. POLLS (4) ================= */

        mk(43, 'polls', 'Two-option poll, live',
           'Running poll with time remaining',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Growth Notes', handle: 'growthnotes', badge: 'blue' },
               post: { text: 'Which do you actually read first in a newsletter?' },
               metrics: { replies: 210, reposts: 89, likes: 1400, views: 84000 },
               poll: {
                   options: [
                       { label: 'The subject line only', votes: 3200 },
                       { label: 'The whole thing', votes: 1800 }
                   ],
                   totalVotes: 5000, ended: false, remaining: '22h'
               }
           }),

        mk(44, 'polls', 'Four-option poll, closed',
           'Final results state',
           {
               theme: 'dim', layout: 'detail',
               author: { name: 'Tobi Adeyemi', handle: 'tobiwrites', badge: 'blue' },
               post: { text: 'What actually slows your team down the most?' },
               metrics: { replies: 890, reposts: 340, likes: 4100, bookmarks: 1200, views: 240000 },
               poll: {
                   options: [
                       { label: 'Unclear requirements', votes: 12400 },
                       { label: 'Code review latency', votes: 4200 },
                       { label: 'Flaky tests', votes: 3100 },
                       { label: 'Meetings', votes: 6800 }
                   ],
                   totalVotes: 26500, ended: true, remaining: ''
               }
           }),

        mk(45, 'polls', 'Poll with a quote tweet',
           'Poll plus quoted context — a shape most tools cannot compose',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'Policy Watch', handle: 'policywatch', badge: 'blue' },
               post: { text: 'Given the correction below, what should happen next?' },
               metrics: { replies: 2100, reposts: 890, likes: 6100, views: 420000 },
               quoted: quo('The Daily Ledger', 'dailyledger', 'gold', 'CORRECTION: The correct figure is £4.2m, not £4.2bn.', 12 * HOUR),
               poll: {
                   options: [
                       { label: 'Full retraction', votes: 8900 },
                       { label: 'Correction is enough', votes: 2100 },
                       { label: 'Independent review', votes: 5400 }
                   ],
                   totalVotes: 16400, ended: false, remaining: '1d'
               }
           }),

        mk(46, 'polls', 'Mobile poll, dark',
           'Phone width, three options, unverified',
           {
               theme: 'dark', viewport: 'mobile', layout: 'feed',
               author: { name: 'nat', handle: 'natgotjokes', badge: 'none' },
               post: { text: 'be honest' },
               metrics: { replies: 340, reposts: 120, likes: 8900, views: 210000 },
               poll: {
                   options: [
                       { label: 'Yes', votes: 4200 },
                       { label: 'No', votes: 1100 },
                       { label: 'I am not answering this', votes: 6800 }
                   ],
                   totalVotes: 12100, ended: false, remaining: '3h'
               }
           }),

        /* ================= 12. EDUCATION & MEDIA LITERACY (4) ================= */

        mk(47, 'education', 'Spot the fake — the giveaways',
           'A classroom example with the tells deliberately present',
           {
               theme: 'light', layout: 'detail',
               author: { name: 'BBC Breaking News', handle: 'BBCBreakinq', badge: 'blue' },
               post: { text: 'BREAKING: Government announces all student loans will be cancelled tomorrow morning. Share before this gets taken down!!' },
               metrics: { replies: 12000, reposts: 84000, quotes: 22000, likes: 140000, views: 22000000 },
               note: {
                   headline: 'Readers added context',
                   body: 'This account is not the BBC. The handle uses a lowercase "q" in place of the final "g". Genuine breaking-news accounts do not ask readers to share before a post is "taken down".',
                   helpful: true
               }
           }),

        mk(48, 'education', 'Engagement bait, annotated',
           'Teaching the ratio pattern',
           {
               theme: 'dim', layout: 'detail',
               author: { name: 'Daily Facts', handle: 'dailyfactsfeed', badge: 'none' },
               post: { text: '97% of people cannot solve this. Reply with your answer and I will tell you if you are a genius.' },
               metrics: { replies: 240000, reposts: 4100, quotes: 18000, likes: 12000, views: 34000000 },
               note: {
                   headline: 'Readers added context',
                   body: 'Posts of this format are designed to maximise replies, which the algorithm weights heavily. The "97%" figure has no source.',
                   helpful: true
               }
           }),

        mk(49, 'education', 'Source-checking exercise',
           'A plausible statistic with no citation, for classroom use',
           {
               theme: 'light', viewport: 'mobile', layout: 'detail',
               author: { name: 'Health Insights', handle: 'healthinsightsdaily', badge: 'none' },
               post: { text: 'A new study shows people who skip breakfast are 74% more likely to develop heart disease.' },
               metrics: { replies: 4100, reposts: 12000, quotes: 3400, likes: 22000, views: 4800000 },
               note: {
                   headline: 'Readers added context',
                   body: 'No study is linked. The figure appears to come from an observational study which found a correlation, not a cause, and which controlled for neither smoking nor income.',
                   helpful: true
               }
           }),

        mk(50, 'education', 'Before and after a correction',
           'Thread showing a claim then its retraction — media-literacy staple',
           {
               theme: 'light', layout: 'feed',
               author: { name: 'Northgate Herald', handle: 'northgateherald', badge: 'gold' },
               post: { text: 'Council votes to close all six libraries by March.' },
               metrics: { replies: 8900, reposts: 22000, quotes: 4100, likes: 12000, views: 6100000 },
               thread: [
                   thr('UPDATE: The council has clarified that the vote concerned a consultation on opening hours, not closures. Our original post was wrong and we have deleted the article.', 1200)
               ]
           })
    ];

    if (typeof module !== 'undefined' && module.exports) {
        module.exports = window.ITV_X_TEMPLATES;
    }
})(typeof window !== 'undefined' ? window : globalThis);
